import { Checkbox, Flex, Form, Input, Tag, Tooltip, Typography } from 'antd';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import { OrganizationOcppSubprotocolField } from '~/app/components/organization/OrganizationOcppSubprotocolField';
import {
  MAX_CSMS_SECONDARIES,
  type ExternalCsmsFormState,
} from '~/app/components/organization/organizationExternalCsms.utils';
import { useOrganizationCsms } from './hooks/useOrganizationCsms';

export interface OrganizationCsmsSectionProps {
  value: ExternalCsmsFormState;
  primaryAlreadySaved: boolean;
  savedSecondaryCount: number;
  apiError?: string | null;
  onChange: (next: ExternalCsmsFormState) => void;
  onRemoveSecondary: (index: number) => void;
}

export function OrganizationCsmsSection({
  value,
  primaryAlreadySaved,
  savedSecondaryCount,
  apiError,
  onChange,
  onRemoveSecondary,
}: OrganizationCsmsSectionProps) {
  const { t } = useTranslation('organization');
  const {
    secondaries,
    primaryLocked,
    secondaryLimitReached,
    urlError,
    changeEnableCsms,
    changePrimaryBaseUrl,
    changePrimarySubprotocol,
    changeChargerAuthentication,
    addSecondary,
    changeSecondaryBaseUrl,
    changeSecondarySubprotocol,
    changeSecondaryEnabled,
    removeSecondary,
  } = useOrganizationCsms({
    value,
    savedSecondaryCount,
    onChange,
    onRemoveSecondary,
  });
  const primaryUrlError = urlError(value.csmsBaseUrl);

  return (
    <Flex vertical gap={12}>
      <Typography.Text type="secondary">
        {t('dialog.ocppSectionHint')}
      </Typography.Text>

      <Checkbox
        checked={value.enableCsms}
        disabled={primaryLocked && value.enableCsms}
        onChange={(event) => changeEnableCsms(event.target.checked)}
      >
        {t('dialog.enableCsmsLabel')}
      </Checkbox>
      {primaryLocked && value.enableCsms && (
        <Typography.Text type="secondary">
          {t('dialog.csmsPrimaryLocked')}
        </Typography.Text>
      )}

      {value.enableCsms && (
        <Flex
          vertical
          gap={12}
          style={{
            border: '1px solid #1E6DF6',
            borderLeft: '4px solid #1E6DF6',
            borderRadius: 8,
            padding: 16,
            background: '#F4F8FF',
          }}
        >
          <Flex align="center" gap={8}>
            <Tag color="blue" style={{ margin: 0 }}>
              {t('dialog.csmsPrimaryTitle')}
            </Tag>
            <Typography.Text type="secondary">
              {t('dialog.csmsPrimaryHint')}
            </Typography.Text>
          </Flex>
          <Form.Item
            label={t('dialog.csmsUrlLabel')}
            layout="vertical"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            validateStatus={primaryUrlError ? 'error' : undefined}
            help={primaryUrlError}
            style={{ marginBottom: 0 }}
          >
            <Input
              value={value.csmsBaseUrl}
              placeholder={t('dialog.csmsUrlPlaceholder')}
              onChange={(event) => changePrimaryBaseUrl(event.target.value)}
            />
          </Form.Item>
          <div>
            <Typography.Text strong>
              {t('dialog.subprotocolLabel')}
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              <OrganizationOcppSubprotocolField
                value={value.csmsSubprotocol}
                onChange={changePrimarySubprotocol}
              />
            </div>
          </div>
          <Checkbox
            checked={value.chargerAuthenticationEnabled}
            onChange={(event) =>
              changeChargerAuthentication(event.target.checked)
            }
          >
            {t('dialog.chargerAuthenticationEnabledLabel')}
          </Checkbox>
        </Flex>
      )}

      {value.enableCsms && primaryAlreadySaved && (
        <Flex
          vertical
          gap={12}
          style={{
            border: '1px solid #D48806',
            borderLeft: '4px solid #D48806',
            borderRadius: 8,
            padding: 16,
            background: '#FFF8EB',
          }}
        >
          <Flex justify="space-between" align="center" gap={8} wrap="wrap">
            <Flex align="center" gap={8}>
              <Tag color="gold" style={{ margin: 0 }}>
                {t('dialog.csmsSecondaryTitle')}
              </Tag>
              <Typography.Text type="secondary">
                {t('dialog.csmsSecondaryHint', {
                  count: secondaries.length,
                  max: MAX_CSMS_SECONDARIES,
                })}
              </Typography.Text>
            </Flex>
            <Button
              colorVariant="secondary"
              iconLeft={<IconPlus size={16} stroke={1.75} />}
              disabled={secondaryLimitReached}
              onClick={addSecondary}
            >
              {t('dialog.csmsAddSecondary')}
            </Button>
          </Flex>
          {secondaries.length === 0 && (
            <Typography.Text type="secondary">
              {t('dialog.csmsSecondaryEmpty')}
            </Typography.Text>
          )}
          {secondaries.map((endpoint, index) => {
            const secondaryUrlError = urlError(endpoint.baseUrl);
            return (
              <Flex
                key={endpoint.id ?? `draft-${index}`}
                gap={8}
                align="center"
                style={{
                  border: '1px solid #F0D59A',
                  borderRadius: 8,
                  padding: 12,
                  background: '#fff',
                }}
              >
                <Flex vertical gap={8} style={{ flex: 1 }}>
                  <Typography.Text strong>
                    {t('dialog.csmsSecondaryItem', { index: index + 1 })}
                  </Typography.Text>
                  <Form.Item
                    validateStatus={secondaryUrlError ? 'error' : undefined}
                    help={secondaryUrlError}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      value={endpoint.baseUrl}
                      placeholder={t('dialog.csmsSecondaryUrlPlaceholder')}
                      onChange={(event) =>
                        changeSecondaryBaseUrl(index, event.target.value)
                      }
                    />
                  </Form.Item>
                  <OrganizationOcppSubprotocolField
                    value={endpoint.subprotocol}
                    onChange={(subprotocol) =>
                      changeSecondarySubprotocol(index, subprotocol)
                    }
                  />
                  <Checkbox
                    checked={endpoint.enabled}
                    onChange={(event) =>
                      changeSecondaryEnabled(index, event.target.checked)
                    }
                  >
                    {t('dialog.csmsSecondaryEnabled')}
                  </Checkbox>
                </Flex>
                <Tooltip title={t('dialog.csmsRemoveSecondary')}>
                  <span>
                    <Button
                      type="text"
                      danger
                      aria-label={t('dialog.csmsRemoveSecondary')}
                      icon={<IconTrash size={18} stroke={1.75} />}
                      onClick={() => removeSecondary(index)}
                      style={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        padding: 0,
                        justifyContent: 'center',
                        background: 'transparent',
                        color: '#CF1322',
                        border: 'none',
                        boxShadow: 'none',
                      }}
                    />
                  </span>
                </Tooltip>
              </Flex>
            );
          })}
        </Flex>
      )}

      {value.enableCsms && !primaryAlreadySaved && (
        <Typography.Text type="secondary">
          {t('dialog.csmsSecondaryHidden')}
        </Typography.Text>
      )}

      {apiError && <Typography.Text type="danger">{apiError}</Typography.Text>}
    </Flex>
  );
}
